import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import api from '../utils/axios';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import FileUpload from './FileUpload';
import { X, Phone, MapPin, Palette, MessageSquare } from 'lucide-react';

const ProfileModal = ({ vendor, onClose, onSuccess, updateVendorData }) => {
  const queryClient = useQueryClient();

  // Initialize editFormData with vendor data when component mounts
  const [editFormData, setEditFormData] = useState({
    name: vendor?.name || '',
    description: vendor?.description || '',
    phone: vendor?.phone || '',
    location: vendor?.location || '',
    banner_image_url: vendor?.banner_image_url || '',
    logo_url: vendor?.logo_url || '',
    theme_color: vendor?.theme_color || '#FF6B35',
    promo_message: vendor?.promo_message || '',
    social_links: vendor?.social_links || {},
    preparation_time_min: vendor?.preparation_time_min || 10,
    preparation_time_max: vendor?.preparation_time_max || 15,
  });

  // Handle input changes
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const updateVendorMutation = useMutation(
    async (vendorData) => {
      const response = await api.put('/vendor/profile', vendorData);
      return response.data;
    },
    {
      onSuccess: (updatedVendorData) => {
        // Update the vendor data in AuthContext for real-time updates
        updateVendorData(updatedVendorData);

        // Also invalidate any React Query caches if they exist
        queryClient.invalidateQueries(['vendor-profile']);

        showSuccessToast('Profile updated successfully!', 'profile-update');

        // Call the onSuccess callback to close modal and reset state
        if (onSuccess) {
          onSuccess();
        }
      },
      onError: (error) => {
        showErrorToast(
          error.response?.data?.message || 'Failed to update profile',
          'profile-update'
        );
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    updateVendorMutation.mutate(editFormData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Edit Profile</h2>
          <button
            onClick={onClose}
            className="text-secondary-400 hover:text-secondary-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Restaurant Name *
              </label>
              <input
                type="text"
                name="name"
                value={editFormData.name}
                onChange={handleEditInputChange}
                required
                className="input-field"
                placeholder="Enter restaurant name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Phone Number
              </label>
              <div className="relative">
                <Phone className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                <input
                  type="tel"
                  name="phone"
                  value={editFormData.phone}
                  onChange={handleEditInputChange}
                  className="input-field pl-10"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={editFormData.description}
              onChange={handleEditInputChange}
              rows="3"
              className="input-field"
              placeholder="Tell customers about your restaurant..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Location
            </label>
            <div className="relative">
              <MapPin className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
              <input
                type="text"
                name="location"
                value={editFormData.location}
                onChange={handleEditInputChange}
                className="input-field pl-10"
                placeholder="Restaurant address"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Banner Image
              </label>
              <FileUpload
                onUploadSuccess={(file) => {
                  setEditFormData((prev) => ({
                    ...prev,
                    banner_image_url: file.url,
                  }));
                }}
                onUploadError={(error) => {
                  console.error('Banner upload error:', error);
                }}
                multiple={false}
                placeholder="Upload banner image"
                showPreview={true}
                existingImageUrl={editFormData.banner_image_url}
              />
              <input
                type="hidden"
                name="banner_image_url"
                value={editFormData.banner_image_url}
                onChange={handleEditInputChange}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Logo
              </label>
              <FileUpload
                onUploadSuccess={(file) => {
                  setEditFormData((prev) => ({
                    ...prev,
                    logo_url: file.url,
                  }));
                }}
                onUploadError={(error) => {
                  console.error('Logo upload error:', error);
                }}
                multiple={false}
                placeholder="Upload logo image"
                showPreview={true}
                existingImageUrl={editFormData.logo_url}
              />
              <input
                type="hidden"
                name="logo_url"
                value={editFormData.logo_url}
                onChange={handleEditInputChange}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Theme Color
              </label>
              <div className="relative">
                <Palette className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                <input
                  type="color"
                  name="theme_color"
                  value={editFormData.theme_color}
                  onChange={handleEditInputChange}
                  className="input-field pl-10 h-12"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Promo Message
              </label>
              <div className="relative">
                <MessageSquare className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                <input
                  type="text"
                  name="promo_message"
                  value={editFormData.promo_message}
                  onChange={handleEditInputChange}
                  className="input-field pl-10"
                  placeholder="Special offer message"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Preparation Time (Min)
              </label>
              <input
                type="number"
                name="preparation_time_min"
                value={editFormData.preparation_time_min}
                onChange={handleEditInputChange}
                min="0"
                className="input-field"
                placeholder="10"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Preparation Time (Max)
              </label>
              <input
                type="number"
                name="preparation_time_max"
                value={editFormData.preparation_time_max}
                onChange={handleEditInputChange}
                min="0"
                className="input-field"
                placeholder="15"
              />
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              className="btn-primary flex-1"
              disabled={updateVendorMutation.isLoading}
            >
              {updateVendorMutation.isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                'Update Profile'
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileModal;
